import React, { FC } from 'react';
import { FormProvider, useFieldArray, useWatch } from 'react-hook-form';

import { translate } from '@utils/locale';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from '@mui/material';
import { PlusIcon, TrashIcon } from '@assets/icons';
import { CustomSwitch, RhfTextField } from '@components';
import {
  LONG_FIELD_MAX_LENGTH,
  PreIPODealStatus,
  TRANSACTION_SHORT_DESCRIPTION_LENGTH
} from '@constants/common';
import { ToastTypes } from '@type/toast';
import { colors } from '@constants/colors';
import { formatDecimalAmount, getDecimalNumbersOnly } from '@utils/common';
import displayToast from '@components/toast/customToast';
import DateTimePicker from '@components/date-picker/DateTimePicker';

import { PreIPOTransactionDetailsProps } from './types';
import { isPreIPOBottomActionDisabled } from '../../common';
import {
  defaultDpChargesValue,
  defaultStampDutyValue
} from '@modules/pre-ipo-deals/update-share-price/constants';

const PreIPOTransactionDetails: FC<PreIPOTransactionDetailsProps> = ({
  methods,
  control,
  setValue,
  errors,
  setError,
  handleSaveAsDraftClick,
  isUpdatePreIPOLoading
}) => {
  const { getValues } = methods;

  const [isSharePriceAvailable, minInvestment, maxInvestment] = useWatch({
    control: control,
    name: ['isSharePriceAvailable', 'minInvestment', 'maxInvestment']
  });

  const {
    fields: chargesAndFeesFields,
    append: appendChargesAndFeesDetails,
    remove: removeChargesAndFeesDetails
  } = useFieldArray({
    control,
    name: 'chargesAndFees'
  });

  const handleAddNewChargesAndFeesDetails = async () => {
    methods.clearErrors('chargesAndFees');
    const lastIndex = chargesAndFeesFields.length - 1;
    const lastUpperLimit = getValues(`chargesAndFees.${lastIndex}.upperLimit`);
    const lastRow = getValues(`chargesAndFees.${lastIndex}`);

    //Validation1: Check if upperLimit is filled before adding new row
    if (!lastUpperLimit) {
      setError(`chargesAndFees.${lastIndex}.upperLimit`, {
        type: 'manual',
        message: '*required'
      });
      return;
    }

    // Validation2: Ensure upperLimit is greater than lowerLimit

    if (
      lastRow &&
      Number(getDecimalNumbersOnly(lastRow.upperLimit)) <=
        Number(getDecimalNumbersOnly(lastRow.lowerLimit))
    ) {
      setError(`chargesAndFees.${lastIndex}.upperLimit`, {
        type: 'manual',
        message: 'exceed'
      });
      return;
    }

    const newLowerLimit = lastUpperLimit || '0';

    appendChargesAndFeesDetails({
      lowerLimit: formatDecimalAmount(newLowerLimit),
      upperLimit: '',
      tax: '',
      documentationChargesPercent: '',
      documentationChargesAmount: '',
      platformChargesPercent: '',
      platformChargesAmount: '',
      sharePrice: '',
      stampDuty: defaultStampDutyValue,
      dpCharges: defaultDpChargesValue
    });
  };

  const handleRemoveChargesAndFeesDetails = (index: number) => {
    removeChargesAndFeesDetails(index);
    if (index === 0) {
      appendChargesAndFeesDetails({
        lowerLimit: minInvestment,
        upperLimit: '',
        tax: '',
        documentationChargesPercent: '',
        documentationChargesAmount: '',
        platformChargesPercent: '',
        platformChargesAmount: '',
        sharePrice: '',
        stampDuty: defaultStampDutyValue,
        dpCharges: defaultDpChargesValue
      });
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      event.target.value === 'shareAvailable' &&
      minInvestment === '' &&
      maxInvestment === ''
    ) {
      displayToast(
        ToastTypes.ERROR,
        'Before setting share price and details kindly fill in the minimum and maximum investment'
      );
      return;
    }
    setValue('isSharePriceAvailable', event.target.value === 'shareAvailable');
    if (event.target.value === 'shareAvailable') {
      appendChargesAndFeesDetails({
        lowerLimit: minInvestment,
        upperLimit: '',
        tax: '',
        documentationChargesPercent: '',
        documentationChargesAmount: '',
        platformChargesPercent: '',
        platformChargesAmount: '',
        sharePrice: '',
        stampDuty: defaultStampDutyValue,
        dpCharges: defaultDpChargesValue
      });
    } else {
      removeChargesAndFeesDetails();
      setValue('sharePriceDate', null);
      methods.clearErrors('sharePriceDate');
      setValue('sharePriceVisibility', false);
    }
  };

  return (
    <div className="py-[24px] px-5">
      {/* Share Available or Not Available Selection */}
      <FormProvider {...methods}>
        <form>
          <>
            <div className="pb-[24px] text-xl font-semibold">
              {translate(
                'preIpoDeals.createPreIPODeals.transactionDetails.updateSharePrice'
              )}
            </div>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="isSharePriceAvailable"
                value={
                  isSharePriceAvailable ? 'shareAvailable' : 'shareNotAvailable'
                }
                onChange={handleRadioChange}>
                <FormControlLabel
                  value="shareAvailable"
                  control={<Radio />}
                  label={translate(
                    'preIpoDeals.createPreIPODeals.transactionDetails.sharePriceAvailable'
                  )}
                />
                <FormControlLabel
                  value="shareNotAvailable"
                  control={<Radio />}
                  label={translate(
                    'preIpoDeals.createPreIPODeals.transactionDetails.sharePriceNotAvailable'
                  )}
                />
              </RadioGroup>
            </FormControl>
            {/* Case1: Share Price Not Available */}
            <br />
            {!isSharePriceAvailable && (
              <Box my={3} display="inline-block">
                <Alert
                  severity="info"
                  sx={{ display: 'inline-flex', alignItems: 'start' }}>
                  {translate(
                    'preIpoDeals.createPreIPODeals.transactionDetails.notAvailableInfoPart1'
                  )}
                  <strong>
                    "
                    {translate(
                      'preIpoDeals.createPreIPODeals.transactionDetails.notAvailableInfoPart2'
                    )}
                    "
                  </strong>
                  {translate(
                    'preIpoDeals.createPreIPODeals.transactionDetails.notAvailableInfoPart3'
                  )}
                </Alert>
              </Box>
            )}
          </>
          <br />
          {isSharePriceAvailable && (
            <>
              <div className="mt-[20px] text-base font-medium">
                {translate(
                  'preIpoDeals.createPreIPODeals.transactionDetails.title'
                )}
              </div>
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex flex-row gap-4  items-center mt-3">
                  <div className="text-base font-normal text-black/60">
                    {translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.shareDetailsVisibility'
                    )}
                  </div>
                  <CustomSwitch
                    control={control}
                    name={'sharePriceVisibility'}
                    defaultValue={getValues('sharePriceVisibility')}
                    handleChange={event => {
                      const checked = event.target.checked;
                      setValue('sharePriceVisibility', checked);
                    }}
                  />
                </div>
                <div className="w-[400px]">
                  <DateTimePicker
                    name={'sharePriceDate'}
                    placeholder={translate(
                      'preIpoDeals.createPreIPODeals.preIPODetails.shareDetailsTimeStamp'
                    )}
                    control={control}
                  />
                </div>
              </div>
              <div>
                <div className="overflow-x-auto mb-7">
                  <div className=" min-w-[1200px]">
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      className="mb-2 font-semibold">
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.lowerLimit'
                        )}
                      </Grid>
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.upperLimit'
                        )}
                      </Grid>
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.sharePrice'
                        )}
                      </Grid>
                      <Grid item xs={2.2}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.documentationCharges'
                        )}
                      </Grid>
                      <Grid item xs={2.2}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.platformCharges'
                        )}
                      </Grid>
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.tax'
                        )}
                      </Grid>
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.stampDuty'
                        )}
                      </Grid>
                      <Grid item xs={1.1}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.transactionDetails.tableHeader.dpCharges'
                        )}
                      </Grid>
                    </Grid>
                    <div className="mb-[20px]">
                      {chargesAndFeesFields.map((field, index) => (
                        <div key={field.id}>
                          <Grid
                            container
                            spacing={2}
                            alignItems="flex-start"
                            mb={1}>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.lowerLimit`}
                                placeholder="Lower Limit"
                                errors={errors}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                isDisabled={true}
                                showCurrency
                                isIndianRupee
                              />
                            </Grid>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.upperLimit`}
                                placeholder="Upper Limit"
                                errors={{
                                  [`chargesAndFees.${index}.upperLimit`]:
                                    errors?.chargesAndFees?.[index]?.upperLimit
                                }}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                isDisabled={
                                  index !== chargesAndFeesFields.length - 1
                                }
                                helperText={
                                  errors?.chargesAndFees?.[index]?.upperLimit
                                    ?.message || ''
                                }
                                onChange={event => {
                                  methods.clearErrors(
                                    `chargesAndFees.${index}.upperLimit`
                                  );
                                  setValue(
                                    `chargesAndFees.${index}.upperLimit`,
                                    formatDecimalAmount(
                                      getDecimalNumbersOnly(event.target.value)
                                    )
                                  );
                                }}
                                showCurrency
                                isIndianRupee
                              />
                            </Grid>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.sharePrice`}
                                errors={{
                                  [`chargesAndFees.${index}.sharePrice`]:
                                    errors?.chargesAndFees?.[index]?.sharePrice
                                }}
                                onChange={event => {
                                  methods.clearErrors(
                                    `chargesAndFees.${index}.sharePrice`
                                  );
                                  if (event.target.value === '') {
                                    setValue(
                                      `chargesAndFees.${index}.sharePrice`,
                                      ''
                                    );
                                  } else
                                    setValue(
                                      `chargesAndFees.${index}.sharePrice`,
                                      formatDecimalAmount(
                                        getDecimalNumbersOnly(
                                          event.target.value
                                        )
                                      )
                                    );
                                }}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                placeholder="e.g. 1.77"
                                label="Share Price"
                                showCurrency
                                isIndianRupee
                              />
                            </Grid>
                            <Grid item xs={2.2}>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <RhfTextField
                                    name={`chargesAndFees.${index}.documentationChargesPercent`}
                                    onChange={event => {
                                      setValue(
                                        `chargesAndFees.${index}.documentationChargesPercent`,
                                        formatDecimalAmount(
                                          getDecimalNumbersOnly(
                                            event.target.value
                                          )
                                        )
                                      );
                                    }}
                                    label="% Charge"
                                    placeholder="e.g. 1.77"
                                    errors={errors}
                                    control={control}
                                    wrapperClass={'pt-6  w-full'}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <RhfTextField
                                    name={`chargesAndFees.${index}.documentationChargesAmount`}
                                    onChange={event => {
                                      setValue(
                                        `chargesAndFees.${index}.documentationChargesAmount`,
                                        formatDecimalAmount(
                                          getDecimalNumbersOnly(
                                            event.target.value
                                          )
                                        )
                                      );
                                    }}
                                    label="Fixed Charge"
                                    placeholder="e.g. 2500"
                                    errors={errors}
                                    control={control}
                                    wrapperClass={'pt-6  w-full'}
                                    showCurrency
                                    isIndianRupee
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={2.2}>
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <RhfTextField
                                    name={`chargesAndFees.${index}.platformChargesPercent`}
                                    onChange={event => {
                                      setValue(
                                        `chargesAndFees.${index}.platformChargesPercent`,
                                        formatDecimalAmount(
                                          getDecimalNumbersOnly(
                                            event.target.value
                                          )
                                        )
                                      );
                                    }}
                                    label="% Charge"
                                    placeholder="e.g. 1.77"
                                    errors={errors}
                                    control={control}
                                    wrapperClass={'pt-6  w-full'}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <RhfTextField
                                    name={`chargesAndFees.${index}.platformChargesAmount`}
                                    onChange={event => {
                                      setValue(
                                        `chargesAndFees.${index}.platformChargesAmount`,
                                        formatDecimalAmount(
                                          getDecimalNumbersOnly(
                                            event.target.value
                                          )
                                        )
                                      );
                                    }}
                                    label="Fixed Charge"
                                    placeholder="e.g. 2500"
                                    errors={errors}
                                    control={control}
                                    wrapperClass={'pt-6  w-full'}
                                    showCurrency
                                    isIndianRupee
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.tax`}
                                onChange={event => {
                                  setValue(
                                    `chargesAndFees.${index}.tax`,
                                    formatDecimalAmount(
                                      getDecimalNumbersOnly(event.target.value)
                                    )
                                  );
                                }}
                                errors={errors}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                label="% tax"
                                placeholder="e.g. 1.77"
                              />
                            </Grid>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.stampDuty`}
                                onChange={event => {
                                  setValue(
                                    `chargesAndFees.${index}.stampDuty`,
                                    formatDecimalAmount(
                                      getDecimalNumbersOnly(event.target.value),
                                      5
                                    )
                                  );
                                }}
                                errors={errors}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                label="% stampDuty"
                                placeholder="e.g. 0.15"
                              />
                            </Grid>
                            <Grid item xs={1.1}>
                              <RhfTextField
                                name={`chargesAndFees.${index}.dpCharges`}
                                onChange={event => {
                                  setValue(
                                    `chargesAndFees.${index}.dpCharges`,
                                    formatDecimalAmount(
                                      getDecimalNumbersOnly(event.target.value),
                                      5
                                    )
                                  );
                                }}
                                errors={errors}
                                control={control}
                                wrapperClass={'pt-6  w-full'}
                                label="% dpCharges"
                                placeholder="e.g. 0.05"
                              />
                            </Grid>
                            <Grid item xs={1}>
                              {/* Trash Icon for the last row */}
                              {index === chargesAndFeesFields.length - 1 && (
                                <TrashIcon
                                  className="mt-6 w-6 h-6 cursor-pointer trash-icon"
                                  onClick={() =>
                                    handleRemoveChargesAndFeesDetails(index)
                                  }
                                />
                              )}
                            </Grid>
                          </Grid>
                        </div>
                      ))}
                      {errors?.chargesAndFees?.[chargesAndFeesFields.length - 1]
                        ?.upperLimit &&
                        errors?.chargesAndFees?.[
                          chargesAndFeesFields.length - 1
                        ]?.upperLimit?.message === '*required' && (
                          <div className="my-4 w-fit">
                            <Alert
                              severity="error"
                              sx={{
                                fontSize: '0.875rem',
                                alignItems: 'flex-start'
                              }}>
                              Upper limit is required before adding a new
                              bracket
                            </Alert>
                          </div>
                        )}
                      {errors?.chargesAndFees?.[chargesAndFeesFields.length - 1]
                        ?.upperLimit?.message === 'exceed' && (
                        <div className="my-4 w-fit">
                          <Alert
                            severity="error"
                            sx={{
                              fontSize: '0.875rem',
                              alignItems: 'flex-start'
                            }}>
                            Upper limit should exceed the lower limit
                          </Alert>
                        </div>
                      )}

                      {errors?.chargesAndFees?.[chargesAndFeesFields.length - 1]
                        ?.upperLimit?.message === 'mismatch' && (
                        <div className="my-4 w-fit">
                          <Alert
                            severity="error"
                            sx={{
                              fontSize: '0.875rem',
                              alignItems: 'flex-start'
                            }}>
                            Final upper limit must be equal to max investment
                            amount of ₹{maxInvestment}
                          </Alert>
                        </div>
                      )}
                      {errors?.chargesAndFees?.[chargesAndFeesFields.length - 1]
                        ?.sharePrice?.message === 'error' && (
                        <div className="my-4 w-fit">
                          <Alert
                            severity="error"
                            sx={{
                              fontSize: '0.875rem',
                              alignItems: 'flex-start'
                            }}>
                            Share Price is required and cannot be Zero
                          </Alert>
                        </div>
                      )}
                      <div>
                        <div className="group inline-block">
                          <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            style={{
                              marginTop: 16
                            }}
                            className="group-hover:text-white group-hover:bg-brightBlue"
                            startIcon={<PlusIcon />}
                            onClick={handleAddNewChargesAndFeesDetails}>
                            {translate(
                              'preIpoDeals.createPreIPODeals.transactionDetails.addAnotherBracket'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="mt-[52px] text-base font-medium">
                  {translate('deals.carryInformation')}
                </div>
                <div className="mb-8 text-xs font-normal text-grey">
                  {translate('deals.additionalChargesDescription')}
                </div>

                <div className="flex flex-row gap-4 items-start my-4 w-[75%]">
                  <RhfTextField
                    name={'carryShortDescription'}
                    placeholder={translate('deals.shortDescription')}
                    control={control}
                    wrapperClass="w-1/3"
                    rows={3}
                    multiline={true}
                    maxLength={TRANSACTION_SHORT_DESCRIPTION_LENGTH}
                    showCharacterLimit={true}
                  />

                  <RhfTextField
                    name={'carryLongDescription'}
                    placeholder={translate('deals.longDescription')}
                    control={control}
                    wrapperClass="w-1/3"
                    rows={3}
                    multiline={true}
                    maxLength={LONG_FIELD_MAX_LENGTH}
                    showCharacterLimit={true}
                  />
                </div>
              </div>
            </>
          )}{' '}
          <div className="fixed right-0 bottom-0 p-12 w-auto">
            <Button
              disabled={isPreIPOBottomActionDisabled(
                getValues('status') as PreIPODealStatus
              )}
              style={{
                width: '136px',
                backgroundColor: colors.gray88,
                color: isPreIPOBottomActionDisabled(
                  getValues('status') as PreIPODealStatus
                )
                  ? colors.gray
                  : colors.black
              }}
              variant={'contained'}
              onClick={handleSaveAsDraftClick}
              sx={{
                textTransform: 'uppercase'
              }}>
              {isUpdatePreIPOLoading ? (
                <CircularProgress size="1rem" color="inherit" />
              ) : (
                translate('saveAsDraft')
              )}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default PreIPOTransactionDetails;
